import { BitmapFont, Cache, FillGradient, Text, type BitmapText, type TextStyleOptions } from "pixi.js";
import { LocalizedText } from "@slot/game-shell";
import type { LocalizationService } from "@slot/game-shell";

/** Roboto Black from the shell's fonts (loaded with `shell-ui` before the scene exists). */
export const FONT = "Roboto, system-ui, sans-serif";

/**
 * The plaques' amounts: Rubik One, the mock-up's numerals (`assets-src/game-shared-visuals/fonts`, SIL OFL; the asset
 * build registers it by its file's name), Roboto if it is missing.
 */
const PLAQUE_FONT = `RubikOne, ${FONT}`;

/** Rubik One's digits stand at its cap height: this fraction of the font's size. */
export const PLAQUE_CAP_HEIGHT = 0.7;

/** A vertical gold gradient for headings (the mock-up's gold-to-orange lettering). */
function goldFill(): FillGradient {
  return new FillGradient({
    type: "linear",
    start: { x: 0, y: 0 },
    end: { x: 0, y: 1 },
    colorStops: [
      { offset: 0, color: 0xfffbd8 },
      { offset: 0.4, color: 0xffd84a },
      { offset: 0.75, color: 0xff9a12 },
      { offset: 1, color: 0xd45a06 },
    ],
    textureSpace: "local",
  });
}

/**
 * The game's type: cream values on a dark red outline (the mock-up's coins), gold headings, white amounts, and each
 * pot's name in its colour on a dark outline.
 */
export const TEXT = {
  heading: () => ({ fontFamily: FONT, fontWeight: "900", fill: goldFill(), stroke: { color: 0x5a0a04, width: 12, join: "round" }, align: "center" }) satisfies TextStyleOptions,
  amount: { fontFamily: FONT, fontWeight: "900", fill: 0xffffff, stroke: { color: 0x7a1004, width: 14, join: "round" }, align: "center" } satisfies TextStyleOptions,
  body: { fontFamily: FONT, fontWeight: "700", fill: 0xffe9c0, stroke: { color: 0x4a0606, width: 6, join: "round" }, align: "center" } satisfies TextStyleOptions,
  bigWinAmount: {
    fontFamily: FONT,
    fontWeight: "900",
    fill: 0xffffff,
    stroke: { color: 0x9a1006, width: 16, join: "round" },
    dropShadow: { color: 0x2a0000, alpha: 0.6, blur: 8, distance: 6, angle: Math.PI / 2 },
    align: "center",
  } satisfies TextStyleOptions,
} as const;

/** The bitmap fonts the cells and the meters draw with: built once, from the loaded Roboto, so a rolling cell's label costs no canvas redraw. */
export const BITMAP_FONTS = {
  /** A coin's value: cream on dark red. */
  coin: "CoinHoldCoinValue",
  /** A plaque's amount: Rubik One, cream on near-black, as the mock-up's. */
  plaque: "CoinHoldPlaqueAmount",
  /** A multiplier badge's "×n" and the meter's digits. */
  badge: "CoinHoldBadge",
} as const;

const DIGITS: (string | string[])[] = [["0", "9"], ".", ",", " ", "'", "×", "x", "+", "-"];

/**
 * The artist's golden digits for the coins' values and the multipliers (the handoff's `coin_digits`, 2026-10-12): a
 * bitmap font loaded with the game's bundle (`fonts/coin-digits`), named by its face. It holds 0–9, `x`, `.`, `,` and
 * the space only: a text with anything else (another locale's grouping, say) is drawn in the Roboto font the place had,
 * as is every text while the font is not in.
 * - `sizeRatio`: its size for that font's, the mock-up's on a coin (its "4.00" about 0.55 of the coin's width: 64 of the
 *   coin image's pixels, as the Roboto at 96 drew it; the user, 2026-10-12: the artist's 110 mock-up px, 57, too small).
 * - `centreShift`: how far below its label's anchor the digits' paint falls, a share of the font size, lifted by the
 *   label's pivot (the user: they sat low). Pixi centres a loaded font's line with its baseline offset added (340 − 241),
 *   which puts a glyph's top 120.5 above the anchor; the digits' paint (rows 0 to 293 of its 340, measured on its page)
 *   is centred 147 below that top: 26.5 at the font's 300.
 */
export const COIN_DIGITS = { fontFamily: "CoinDigits", chars: "0123456789x., ", sizeRatio: 2 / 3, centreShift: 26.5 / 300 } as const;

/** The spaces a money formatter may group digits with (no-break, narrow no-break, thin): the digits' own space. */
const GROUP_SPACES = /[\u00a0\u202f\u2009]/g;

/**
 * `text` as a coin's value or a multiplier is drawn, where the Roboto `fallback` font would draw it at `fontSize`: in
 * the golden digits, at their size for that, when it has every character, its label's pivot (`pivotY`) lifting their
 * paint onto its anchor; else in `fallback`, as it is.
 */
export function coinText(
  text: string,
  fontSize: number,
  fallback: string = BITMAP_FONTS.coin,
): { text: string; fontFamily: string; fontSize: number; pivotY: number } {
  const spaced = text.replace(GROUP_SPACES, " ");
  const drawable = Cache.has(`${COIN_DIGITS.fontFamily}-bitmap`) && [...spaced].every((char) => COIN_DIGITS.chars.includes(char));
  if (!drawable) return { text, fontFamily: fallback, fontSize, pivotY: 0 };
  const size = fontSize * COIN_DIGITS.sizeRatio;
  return { text: spaced, fontFamily: COIN_DIGITS.fontFamily, fontSize: size, pivotY: size * COIN_DIGITS.centreShift };
}

/** Sets `label` to `text` as `coinText` draws it. */
export function setCoinText(label: BitmapText, text: string, fontSize: number, fallback: string = BITMAP_FONTS.coin): void {
  const drawn = coinText(text, fontSize, fallback);
  if (label.style.fontFamily !== drawn.fontFamily) label.style.fontFamily = drawn.fontFamily;
  if (label.style.fontSize !== drawn.fontSize) label.style.fontSize = drawn.fontSize;
  label.text = drawn.text;
  label.pivot.y = drawn.pivotY;
}

let installed = false;

/** Installs the bitmap fonts (idempotent; the first scene does it, once the shell has loaded Roboto and Rubik One). */
export function installBitmapFonts(): void {
  if (installed) return;
  installed = true;
  BitmapFont.install({
    name: BITMAP_FONTS.coin,
    style: { fontFamily: FONT, fontWeight: "900", fontSize: 72, fill: 0xfff3c8, stroke: { color: 0x7a1c04, width: 10, join: "round" } },
    chars: DIGITS,
    resolution: 2,
  });
  BitmapFont.install({
    name: BITMAP_FONTS.plaque,
    style: { fontFamily: PLAQUE_FONT, fontWeight: "normal", fontSize: 64, fill: 0xfff0cb, stroke: { color: 0x210202, width: 10, join: "round" } },
    chars: DIGITS,
    resolution: 2,
  });
  BitmapFont.install({
    name: BITMAP_FONTS.badge,
    style: { fontFamily: FONT, fontWeight: "900", fontSize: 64, fill: 0xffffff, stroke: { color: 0x5a0606, width: 10, join: "round" } },
    chars: DIGITS,
    resolution: 2,
  });
}

/** A localized line, centred at `y`, shrunk to `box` when a language runs long. */
export function label(input: {
  localization: LocalizationService;
  key: string;
  fallback: string;
  style: TextStyleOptions;
  fontSize: number;
  y: number;
  box: { width: number; height: number };
  label: string;
}): LocalizedText {
  const text = new LocalizedText({
    localization: input.localization,
    key: input.key,
    fallback: input.fallback,
    style: { ...input.style, fontSize: input.fontSize },
    label: input.label,
    fitBox: input.box,
  });
  text.position.set(0, input.y);
  return text;
}

/** A line whose string the view sets (amounts, counts), centred at `y`. */
export function value(input: { style: TextStyleOptions; fontSize: number; y: number; label: string }): Text {
  const text = new Text({ text: "", style: { ...input.style, fontSize: input.fontSize }, label: input.label });
  text.anchor.set(0.5);
  text.position.set(0, input.y);
  return text;
}

/** Shrinks `node` (never grows it) so its unscaled width fits `width`. */
export function fitWidth(node: { width: number; scale: { x: number; set(x: number, y?: number): void } }, width: number): void {
  const natural = node.width / (node.scale.x || 1);
  const scale = Math.min(1, width / Math.max(1, natural));
  node.scale.set(scale, scale);
}
